// speech.ts — the EXAMINER's voice + mouth timing, entirely on-device (fleet voice standard:
// device TTS only, cloud audio banned). speechSynthesis speaks the question; while it speaks
// we stream Oculus viseme ids to whoever is listening (the ExaminerAvatar) so the mouth moves
// in time with the words.
//
// Timing source = the utterance's WORD BOUNDARY events (`onboundary` with charIndex): at each
// word we run the vendored TalkingHead lipsync-en text→viseme rules on THAT word and schedule
// its visemes over the gap until the next boundary. Feature-detected: engines that never fire
// boundary events (some webviews/voices) degrade to a generic talking loop for the utterance's
// duration — the avatar still visibly talks, just without per-word sync.
//
// Nothing here touches the network. The dictation (speak-in) side lives in the UI via the
// SpeechRecognition-based components (chat/voice.tsx + the Dictation box in App.tsx).

import { LipsyncEn } from "./vendor/talkinghead/lipsync-en.mjs";

const synth: SpeechSynthesis | undefined =
  typeof window !== "undefined" ? window.speechSynthesis : undefined;

export const ttsAvailable = (): boolean => !!synth;

const lipsync = new LipsyncEn();

export interface SpeakCallbacks {
  /** Streamed Oculus viseme id ('aa','E','I','O','U','PP','FF','TH','DD','kk','CH','SS','nn','RR','sil'). */
  onViseme?: (viseme: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

export interface SpeakHandle {
  cancel: () => void;
}

// Pick the BEST free English voice once (review finding: the first en-GB voice iOS offers is
// the compact, robotic one). Prefer Premium/Enhanced device voices, then the known-good named
// ones, then any en-GB (examiner register), then any en. All on-device, all free.
let cachedVoice: SpeechSynthesisVoice | null | undefined;
function pickVoice(): SpeechSynthesisVoice | null {
  if (cachedVoice !== undefined) return cachedVoice;
  const en = (synth?.getVoices() ?? []).filter((v) => v.lang?.toLowerCase().startsWith("en"));
  const gb = en.filter((v) => v.lang?.toLowerCase().startsWith("en-gb"));
  const quality = (list: SpeechSynthesisVoice[]) =>
    list.find((v) => /premium/i.test(v.name)) ?? list.find((v) => /enhanced/i.test(v.name));
  const named = (list: SpeechSynthesisVoice[], names: string[]) =>
    names.map((n) => list.find((v) => v.name?.startsWith(n))).find(Boolean);
  cachedVoice =
    quality(gb) ?? named(gb, ["Serena", "Kate", "Stephanie"]) ??
    quality(en) ?? named(en, ["Ava", "Samantha", "Allison", "Zoe"]) ??
    gb[0] ?? en[0] ?? null;
  return cachedVoice ?? null;
}
// Voice lists load async in some engines — refresh the cache when they arrive.
// Mandarin model voice — same one-consistent-voice rule as the examiner (Accent Lab 2026-08-27):
// prefer enhanced/premium zh voices (Tingting family on Apple), then any zh. Cached like pickVoice.
let cachedZhVoice: SpeechSynthesisVoice | undefined | null;
function pickZhVoice(): SpeechSynthesisVoice | null {
  if (cachedZhVoice !== undefined) return cachedZhVoice ?? null;
  const zh = (synth?.getVoices() ?? []).filter((v) => v.lang?.toLowerCase().startsWith("zh"));
  const score = (v: SpeechSynthesisVoice) =>
    (/premium|enhanced/i.test(v.name) ? 4 : 0) + (/tingting|meijia|sinji/i.test(v.name) ? 2 : 0) + (v.lang?.toLowerCase() === "zh-cn" ? 1 : 0);
  cachedZhVoice = zh.sort((a, b) => score(b) - score(a))[0] ?? null;
  return cachedZhVoice;
}

/** True when the device offers ANY Chinese voice — the zh model-voice hint keys off this. */
export const hasZhVoice = (): boolean => !!pickZhVoice();

if (synth && "onvoiceschanged" in synth) {
  synth.onvoiceschanged = () => { cachedVoice = undefined; cachedZhVoice = undefined; };
}

/** Rough per-word speaking time at rate ~0.95 — used to scale the relative viseme durations. */
function wordMillis(word: string): number {
  return Math.max(140, 60 * Math.max(2, word.length));
}

const GENERIC_LOOP: string[] = ["aa", "E", "nn", "O", "I", "sil", "E", "aa"];

/**
 * Speak `text` with the device's own TTS, streaming visemes for the avatar.
 * Returns a handle whose cancel() stops the speech AND the mouth.
 */
export function speakExaminer(text: string, cb: SpeakCallbacks = {}, opts?: { lang?: "en" | "zh" }): SpeakHandle {
  const timers: ReturnType<typeof setTimeout>[] = [];
  let genericTimer: ReturnType<typeof setInterval> | null = null;
  let cancelled = false;
  let sawBoundary = false;

  const clearAll = () => {
    for (const t of timers) clearTimeout(t);
    timers.length = 0;
    if (genericTimer) { clearInterval(genericTimer); genericTimer = null; }
  };
  const finish = () => {
    if (cancelled) return;
    cancelled = true;
    clearAll();
    cb.onViseme?.("sil");
    cb.onEnd?.();
  };

  if (!synth || !text.trim()) {
    // No TTS on this platform — the question is still shown as text; report an immediate end.
    setTimeout(() => { cb.onStart?.(); finish(); }, 0);
    return { cancel: finish };
  }

  synth.cancel(); // never overlap examiners
  const u = new SpeechSynthesisUtterance(text);
  u.lang = opts?.lang === "zh" ? "zh-CN" : "en-GB";
  u.rate = opts?.lang === "zh" ? 0.85 : 0.95; // learners need Mandarin slightly slower
  const voice = opts?.lang === "zh" ? pickZhVoice() : pickVoice();
  if (voice) u.voice = voice;

  const scheduleWord = (word: string) => {
    // New word → drop any visemes still queued from the previous one.
    for (const t of timers) clearTimeout(t);
    timers.length = 0;
    let seq;
    try {
      seq = lipsync.wordsToVisemes(lipsync.preProcessText(word));
    } catch {
      return;
    }
    if (!seq || seq.visemes.length === 0) return;
    const total = seq.times[seq.visemes.length - 1] + seq.durations[seq.visemes.length - 1];
    if (!(total > 0)) return;
    const scale = wordMillis(word) / total;
    seq.visemes.forEach((v: string, i: number) => {
      timers.push(setTimeout(() => { if (!cancelled) cb.onViseme?.(v); }, Math.round(seq.times[i] * scale)));
    });
  };

  u.onstart = () => {
    cb.onStart?.();
    // Boundary feature-detect: if no word boundary showed up shortly after start,
    // fall back to a generic talking loop for the rest of the utterance.
    timers.push(setTimeout(() => {
      if (!sawBoundary && !cancelled && !genericTimer) {
        let i = 0;
        genericTimer = setInterval(() => {
          cb.onViseme?.(GENERIC_LOOP[i % GENERIC_LOOP.length]);
          i++;
        }, 130);
      }
    }, 450));
  };
  u.onboundary = (e: SpeechSynthesisEvent) => {
    if (e.name && e.name !== "word") return;
    sawBoundary = true;
    if (genericTimer) { clearInterval(genericTimer); genericTimer = null; }
    const idx = typeof e.charIndex === "number" ? e.charIndex : 0;
    const m = /^[A-Za-z'’-]+/.exec(text.slice(idx));
    if (m) scheduleWord(m[0]);
  };
  u.onend = finish;
  u.onerror = finish;

  synth.speak(u);

  return {
    cancel: () => {
      try { synth.cancel(); } catch { /* ignore */ }
      finish();
    },
  };
}
